import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface FoundingCreditWindow {
  /** True while the member is inside their first 30 days as an approved founding member. */
  active: boolean;
  /** ISO date the 30-day window ends (stacked credits unlock + discount stops). */
  unlockAt: string | null;
  /** Whole days remaining in the window (0 when inactive). */
  daysRemaining: number;
  /** Discount fraction applied to purchases while active (0.5 = 50% off). */
  discount: number;
}

const WINDOW_DAYS = 30;
const DISCOUNT = 0.5;

/**
 * Founding members get a 30-day engagement window starting at approval.
 * While active:
 *  - earned credits are locked; daily credits AND purchased credits spend.
 *  - all credit purchases are 50% off.
 * This mirrors the server-side `founding_window_active()` guard and the
 * `_deduct_credits` spendability rule.
 */
export function useFoundingCreditWindow(): FoundingCreditWindow {
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: ["founding-credit-window", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<{ status: string; approved_at: string | null } | null> => {
      const { data, error } = await supabase
        .from("founding_members")
        .select("status, approved_at")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return (data as { status: string; approved_at: string | null }) ?? null;
    },
  });

  if (!data || data.status !== "approved" || !data.approved_at) {
    return { active: false, unlockAt: null, daysRemaining: 0, discount: 0 };
  }

  const approved = new Date(data.approved_at).getTime();
  const unlock = approved + WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const active = now < unlock;
  const daysRemaining = active ? Math.ceil((unlock - now) / (24 * 60 * 60 * 1000)) : 0;

  return {
    active,
    unlockAt: new Date(unlock).toISOString(),
    daysRemaining,
    discount: active ? DISCOUNT : 0,
  };
}
